import React, { useState } from "react";
import moment from "moment";
import { services } from "../../services";
import { toast } from "react-toastify";
import { User } from "../CandidateStats";

export default function EditUserModal({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    mobileNumber: user.mobileNumber || "",
    dateOfBirth: moment(user.dateOfBirth).format("YYYY-MM-DD"),
    identityProofFile: null as File | null,
    qualificationFile: null as File | null,
    photoFile: null as File | null,
    signatureFile: null as File | null,
  });
  const uploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return services.requestSvc.post("/upload-file", formData);
  };
  const handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);

      let identityProofFileId;
      let qualificationFileId;
      let photoFileId;
      let signatureFileId;

      if (form.identityProofFile) {
        const res = await uploadFile(form.identityProofFile);
        identityProofFileId = res.id;
      }

      if (form.qualificationFile) {
        const res = await uploadFile(form.qualificationFile);
        qualificationFileId = res.id;
      }

      if (form.photoFile) {
        const res = await uploadFile(form.photoFile);
        photoFileId = res.id;
      }

      if (form.signatureFile) {
        const res = await uploadFile(form.signatureFile);
        signatureFileId = res.id;
      }

      await services.requestSvc.post("/update-user", {
        userId: user.id,
        stUserId: (user as any).stUserId,
        name: form.name,
        email: form.email,
        mobileNumber: form.mobileNumber,
        dateOfBirth: form.dateOfBirth,
        identityProofFileId,
        highestQualificationCertificateFileId: qualificationFileId,
        candidatePhotoFileId: photoFileId,
        candidateSignatureFileId: signatureFileId,
      });

      toast.success("Candidate updated successfully");
      onClose();
      window.location.reload();
    } catch {
      toast.error("Failed to update candidate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl shadow-xl p-6">
          {/* HEADER */}
          <h2 className="text-xl font-semibold text-gray-800 mb-5">
            Edit Candidate
          </h2>

          {/* FORM */}
          <div className="space-y-4">
            {/* NAME */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* EMAIL */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* MOBILE */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Mobile Number
              </label>
              <input
                type="text"
                value={form.mobileNumber}
                onChange={(e) => handleChange("mobileNumber", e.target.value)}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* DOB */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {/* Identity Proof */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Identity Proof
              </label>

              {(form.identityProofFile ||
                user.identityProofFile?.downloadUrl) && (
                <img
                  src={
                    form.identityProofFile
                      ? URL.createObjectURL(form.identityProofFile)
                      : user.identityProofFile?.downloadUrl
                  }
                  className="w-24 h-24 object-contain mb-2 border rounded"
                />
              )}

              <input
                type="file"
                onChange={(e) =>
                  handleChange("identityProofFile", e.target.files?.[0] || null)
                }
              />
            </div>

            {/* Qualification Certificate */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Qualification Certificate
              </label>

              {(form.qualificationFile ||
                user.highestQualificationFile?.downloadUrl) && (
                <img
                  src={
                    form.qualificationFile
                      ? URL.createObjectURL(form.qualificationFile)
                      : user.highestQualificationFile?.downloadUrl
                  }
                  className="w-24 h-24 object-contain mb-2 border rounded"
                />
              )}

              <input
                type="file"
                onChange={(e) =>
                  handleChange("qualificationFile", e.target.files?.[0] || null)
                }
              />
            </div>

            {/* Candidate Photo */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Candidate Photo
              </label>

              {(form.photoFile || user.candidatePhotoFile?.downloadUrl) && (
                <img
                  src={
                    form.photoFile
                      ? URL.createObjectURL(form.photoFile)
                      : user.candidatePhotoFile?.downloadUrl
                  }
                  className="w-24 h-24 object-cover mb-2 border rounded"
                />
              )}

              <input
                type="file"
                onChange={(e) =>
                  handleChange("photoFile", e.target.files?.[0] || null)
                }
              />
            </div>

            {/* Signature */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Signature
              </label>

              {(form.signatureFile ||
                user.candidateSignatureFile?.downloadUrl) && (
                <img
                  src={
                    form.signatureFile
                      ? URL.createObjectURL(form.signatureFile)
                      : user.candidateSignatureFile?.downloadUrl
                  }
                  className="w-32 h-16 object-contain mb-2 border rounded"
                />
              )}

              <input
                type="file"
                onChange={(e) =>
                  handleChange("signatureFile", e.target.files?.[0] || null)
                }
              />
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>

            <button
              onClick={handleUpdate}
              disabled={loading}
              className="px-5 py-2 bg-[#4F46E5] text-white rounded-md hover:bg-[#4338CA] transition"
            >
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
